import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CollapsibleSection } from '../../shared/CollapsibleSection';
import { DescriptionChecklist } from '../../shared/DescriptionChecklist';
import { useFirstTimeExpand } from '../../../hooks/useFirstTimeExpand';
import { colors } from '../../../theme/colors';

interface CollapsibleStepInfoProps {
  stepId: string;
  description: string;
  /**
   * When true, default to collapsed even on first view.
   * Used for completed steps where we want a compact view.
   */
  defaultCollapsed?: boolean;
  /**
   * When true, use past-tense title ("What was needed").
   * Used for completed steps.
   */
  isCompleted?: boolean;
}

/**
 * Collapsible step info section.
 * - Auto-expands the first time a user visits a step (unless defaultCollapsed)
 * - Remembers collapsed state for subsequent visits
 * - Renders behind a "What you need" / "What was needed" affordance
 * - Uses on-demand AI extraction for list detection (cached per step)
 */
export const CollapsibleStepInfo: React.FC<CollapsibleStepInfoProps> = ({
  stepId,
  description,
  defaultCollapsed = false,
  isCompleted = false,
}) => {
  const [expanded, setExpanded] = useFirstTimeExpand(`step-info-${stepId}`, {
    defaultExpanded: !defaultCollapsed,
  });

  if (!description) {
    return null;
  }

  const title = isCompleted ? 'What was needed' : 'What you need';
  const accentColor = isCompleted ? colors.gray[400] : colors.blue[400];

  return (
    <CollapsibleSection
      title={title}
      icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
      expanded={expanded}
      onToggle={setExpanded}
      accentColor={accentColor}
    >
      <DescriptionChecklist
        stepId={stepId}
        description={description}
        isCompleted={isCompleted}
        textColor={isCompleted ? colors.gray[500] : colors.gray[600]}
      />
    </CollapsibleSection>
  );
};

export default CollapsibleStepInfo;
